import { type CheckboxChangeEvent } from "antd/lib/checkbox";
import { memo } from "react";

import { BTCheckbox } from "@btnet/core-components";
import { BTPopover } from "@btnet/core-components/BTPopover";

import { calculateTotalIncludingTax } from "utilities/tax/tax.utilities";

import { type IGridCellRenderProps } from "commonComponents/utilities/Grid/Grid.types";
import TextTruncate from "commonComponents/utilities/TextTruncate/TextTruncate";

import { getSetTaxMessage } from "entity/tax/common/tax.types";
import { NoTaxId } from "entity/tax/TaxRate/TaxRate.api.types";

export interface ITaxesCellProps {
    id: number;
    taxGroupId?: number;
    ellipsis?: boolean;
    totalEffectiveRate?: number;
    taxableAmount?: number; // Need to assign this value from whatever entity is using this component. Ex: ownerPrice on estimate is the taxable amount
    newTaxGroupId?: number;
    isTemplate?: boolean;
}

export const TaxesCell: React.FunctionComponent<IGridCellRenderProps<ITaxesCellProps>> = memo(
    function TaxesCell({
        cellValue,
        field,
        isInEdit,
        canEdit,
        onChange,
    }: IGridCellRenderProps<ITaxesCellProps>) {
        const fieldName = `${field.name}-taxGroupId`;
        const checked = !(
            cellValue?.taxGroupId === null ||
            cellValue?.taxGroupId === undefined ||
            cellValue?.taxGroupId === NoTaxId
        );

        const handleChange = (e: CheckboxChangeEvent) => {
            const taxGroupId = e.target.checked ? cellValue?.newTaxGroupId : NoTaxId;
            const effectiveTaxRate =
                e.target.checked && cellValue?.totalEffectiveRate
                    ? cellValue?.totalEffectiveRate
                    : 0;
            const totalWithTax = calculateTotalIncludingTax(
                cellValue?.taxableAmount ?? 0,
                effectiveTaxRate,
            );
            onChange(field.path, {
                ...cellValue,
                taxGroupId,
                totalWithTax,
                totalEffectiveRate: effectiveTaxRate,
            });
        };
        if (!isInEdit) {
            const text = checked ? "Taxable" : "Non-taxable";
            return (
                <div className="TaxCell">
                    <TextTruncate>{text}</TextTruncate>
                </div>
            );
        }

        return (
            <div className="TaxCell">
                <BTPopover
                    content={getSetTaxMessage(cellValue?.isTemplate ?? false)}
                    data-testid={`${fieldName}-taxableTooltip`}
                    disabled={!!cellValue?.newTaxGroupId && cellValue?.newTaxGroupId !== NoTaxId}
                >
                    <div>
                        <BTCheckbox
                            id={fieldName}
                            checked={checked}
                            onChange={handleChange}
                            data-testid={`${field.path}-taxableCheckbox`}
                            disabled={!canEdit}
                        >
                            Taxable
                        </BTCheckbox>
                    </div>
                </BTPopover>
            </div>
        );
    },
);
